import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { fileToDataUrl } from "@/lib/format";
import "@/styles/portfolio.css";
import "@/styles/admin.css";

export default function PostEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const bodyRef = useRef(null);

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [imgBusy, setImgBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiAutofill, setAiAutofill] = useState(false);
  const [aiOverwrite, setAiOverwrite] = useState(false);

  const [form, setForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    body: "",
    cover_image: "",
    cover_filename: "",
    cover_alt: "",
    published: true,
  });

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/posts/${id}`);
        setForm({
          title: data.title || "",
          slug: data.slug || "",
          excerpt: data.excerpt || "",
          body: data.body || "",
          cover_image: data.cover_image || "",
          cover_filename: data.cover_filename || "",
          cover_alt: data.cover_alt || "",
          published: data.published !== false,
        });
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runAiSuggest = async () => {
    if (!form.title.trim()) {
      setError("Bitte erst einen Titel eingeben — die KI braucht ihn als Basis.");
      return null;
    }
    setAiBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/posts/ai-suggest", {
        title: form.title,
        body: form.body,
      });
      setForm((prev) => ({
        ...prev,
        slug: aiOverwrite || !prev.slug ? data.slug : prev.slug,
        excerpt: aiOverwrite || !prev.excerpt ? data.excerpt : prev.excerpt,
        cover_filename:
          aiOverwrite || !prev.cover_filename ? data.cover_filename : prev.cover_filename,
        cover_alt: aiOverwrite || !prev.cover_alt ? data.cover_alt : prev.cover_alt,
      }));
      return data;
    } catch (err) {
      setError(formatApiError(err) || "KI-Vorschlag fehlgeschlagen.");
      return null;
    } finally {
      setAiBusy(false);
    }
  };

  const handleCover = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImgBusy(true);
    setError("");
    try {
      const dataUrl = await fileToDataUrl(f, 1800, 0.86);
      setForm((prev) => ({
        ...prev,
        cover_image: dataUrl,
        cover_filename: prev.cover_filename || f.name,
      }));
    } catch (err) {
      setError("Cover-Bild konnte nicht verarbeitet werden.");
    } finally {
      setImgBusy(false);
      e.target.value = "";
    }
  };

  const handleInlineImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImgBusy(true);
    setError("");
    try {
      const dataUrl = await fileToDataUrl(f, 1600, 0.85);
      const md = `\n\n![${f.name}](${dataUrl})\n\n`;
      const ta = bodyRef.current;
      if (ta) {
        const start = ta.selectionStart ?? form.body.length;
        const end = ta.selectionEnd ?? form.body.length;
        const next = form.body.slice(0, start) + md + form.body.slice(end);
        set("body", next);
        // Restore cursor after the inserted image
        requestAnimationFrame(() => {
          ta.focus();
          const pos = start + md.length;
          ta.setSelectionRange(pos, pos);
        });
      } else {
        set("body", form.body + md);
      }
    } catch (err) {
      setError("Bild konnte nicht eingefügt werden.");
    } finally {
      setImgBusy(false);
      e.target.value = "";
    }
  };

  const removeCover = () => set("cover_image", "");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    // If the AI auto-fill checkbox is on and the user left fields empty,
    // let the LLM fill them in before saving.
    let working = form;
    if (aiAutofill) {
      const needsAi =
        !form.slug.trim() ||
        !form.excerpt.trim() ||
        !form.cover_filename.trim() ||
        !form.cover_alt.trim() ||
        aiOverwrite;
      if (needsAi) {
        const ai = await runAiSuggest();
        if (ai) {
          working = {
            ...form,
            slug: aiOverwrite || !form.slug ? ai.slug : form.slug,
            excerpt: aiOverwrite || !form.excerpt ? ai.excerpt : form.excerpt,
            cover_filename:
              aiOverwrite || !form.cover_filename ? ai.cover_filename : form.cover_filename,
            cover_alt: aiOverwrite || !form.cover_alt ? ai.cover_alt : form.cover_alt,
          };
        }
      }
    }

    const payload = {
      title: working.title.trim(),
      slug: working.slug.trim() || undefined,
      excerpt: working.excerpt.trim(),
      body: working.body,
      cover_image: working.cover_image || null,
      cover_filename: working.cover_filename.trim(),
      cover_alt: working.cover_alt.trim(),
      published: working.published,
    };
    try {
      if (isEdit) {
        await api.patch(`/admin/posts/${id}`, payload);
      } else {
        await api.post("/admin/posts", payload);
      }
      navigate("/admin/posts", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="admin-shell admin-loading">Lädt…</div>;

  return (
    <div className="admin-shell" data-testid="admin-editor">
      <header className="admin-header">
        <div>
          <Link to="/admin" className="admin-back" data-testid="admin-editor-back">
            ← Übersicht
          </Link>
          <h1 className="display admin-h1">
            {isEdit ? "Beitrag bearbeiten." : (
              <>Neuer <span className="italic">Beitrag</span>.</>
            )}
          </h1>
        </div>
      </header>

      {error && <div className="admin-error">{error}</div>}

      <form onSubmit={handleSubmit} className="admin-form admin-editor-form">
        <label className="admin-field">
          <span className="admin-label">Titel</span>
          <input
            type="text"
            className="admin-input admin-input--lg"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            required
            placeholder="Z. B. Drei Reel-Hooks, die in Baden funktionieren"
            data-testid="editor-title"
          />
        </label>

        <div className="admin-ai-panel" data-testid="ai-panel">
          <div className="admin-ai-panel__main">
            <label className="admin-toggle">
              <input
                type="checkbox"
                checked={aiAutofill}
                onChange={(e) => setAiAutofill(e.target.checked)}
                data-testid="ai-autofill-checkbox"
              />
              <span>
                <strong>KI füllt leere Felder beim Speichern</strong>
                <span className="admin-hint">
                  &nbsp;— Slug, Kurzbeschreibung, Cover-Dateiname und Alt-Text werden aus
                  Titel + Body generiert
                </span>
              </span>
            </label>
            {aiAutofill && (
              <label className="admin-toggle admin-toggle--nested">
                <input
                  type="checkbox"
                  checked={aiOverwrite}
                  onChange={(e) => setAiOverwrite(e.target.checked)}
                  data-testid="ai-overwrite-checkbox"
                />
                <span>
                  Auch bereits ausgefüllte Felder überschreiben
                </span>
              </label>
            )}
          </div>
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={runAiSuggest}
            disabled={aiBusy || !form.title.trim()}
            data-testid="ai-suggest-btn"
            title={!form.title.trim() ? "Erst einen Titel eingeben" : "Vorschau anzeigen + Felder vorbefüllen"}
          >
            <span>{aiBusy ? "KI denkt…" : "Jetzt vorschlagen"}</span>
            <span className="btn__arrow">→</span>
          </button>
        </div>

        <label className="admin-field">
          <span className="admin-label">
            Slug <span className="admin-hint">(optional — wird automatisch generiert)</span>
          </span>
          <input
            type="text"
            className="admin-input"
            value={form.slug}
            onChange={(e) => set("slug", e.target.value)}
            placeholder="reel-hooks-baden"
            data-testid="editor-slug"
          />
        </label>

        <label className="admin-field">
          <span className="admin-label">
            Kurzbeschreibung <span className="admin-hint">(2–3 Sätze für die Übersicht)</span>
          </span>
          <textarea
            className="admin-input admin-textarea"
            value={form.excerpt}
            onChange={(e) => set("excerpt", e.target.value)}
            rows={3}
            placeholder="Wird automatisch generiert, wenn du das Feld leer lässt."
            data-testid="editor-excerpt"
          />
        </label>

        <div className="admin-field">
          <span className="admin-label">Cover-Bild</span>
          {form.cover_image ? (
            <div className="admin-cover-preview" data-testid="editor-cover-preview">
              <img src={form.cover_image} alt={form.cover_alt || "Cover-Vorschau"} />
              <div className="admin-cover-actions">
                <label className="admin-link admin-link--accent">
                  Ersetzen
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCover}
                    hidden
                  />
                </label>
                <button
                  type="button"
                  className="admin-link admin-link--danger"
                  onClick={removeCover}
                >
                  Entfernen
                </button>
              </div>
            </div>
          ) : (
            <label className="admin-upload">
              <input
                type="file"
                accept="image/*"
                onChange={handleCover}
                hidden
                data-testid="editor-cover-input"
              />
              <span className="admin-upload__plus">+</span>
              <span className="admin-upload__label">
                {imgBusy ? "Verarbeitet…" : "Cover-Bild hochladen"}
              </span>
              <span className="admin-upload__hint">JPG/PNG · wird komprimiert</span>
            </label>
          )}
        </div>

        <label className="admin-field">
          <span className="admin-label">
            Cover-Dateiname <span className="admin-hint">(SEO — z. B. &bdquo;reels-restaurant-baden.jpg&ldquo;)</span>
          </span>
          <input
            type="text"
            className="admin-input"
            value={form.cover_filename}
            onChange={(e) => set("cover_filename", e.target.value)}
            placeholder="reels-restaurant-baden.jpg"
            data-testid="editor-cover-filename"
          />
        </label>

        <label className="admin-field">
          <span className="admin-label">
            Cover-Alt-Text <span className="admin-hint">(SEO + Barrierefreiheit)</span>
          </span>
          <input
            type="text"
            className="admin-input"
            value={form.cover_alt}
            onChange={(e) => set("cover_alt", e.target.value)}
            placeholder="Instagram Reel Cover für Restaurant in Baden — content.by.max"
            data-testid="editor-cover-alt"
          />
        </label>

        <label className="admin-field">
          <div className="admin-label-row">
            <span className="admin-label">Inhalt</span>
            <label className="admin-inline-btn">
              <input
                type="file"
                accept="image/*"
                onChange={handleInlineImage}
                hidden
                data-testid="editor-inline-image"
              />
              {imgBusy ? "Verarbeitet…" : "+ Bild einfügen"}
            </label>
          </div>
          <textarea
            ref={bodyRef}
            className="admin-input admin-textarea admin-textarea--tall"
            value={form.body}
            onChange={(e) => set("body", e.target.value)}
            required
            rows={20}
            placeholder={`Markdown wird unterstützt:\n\n## Zwischenüberschrift\n\nEin **fetter** Absatz mit *kursivem* Text.\n\n- Erstes Stichwort\n- Zweites Stichwort`}
            data-testid="editor-body"
          />
          <p className="admin-hint admin-hint--block">
            Tipp: Mit <code>##</code> beginnst du eine Zwischenüberschrift,
            <code> **fett**</code> macht Text fett, <code>*kursiv*</code> kursiv.
            Listen mit <code>-</code> am Zeilenanfang.
          </p>
        </label>

        <label className="admin-toggle">
          <input
            type="checkbox"
            checked={form.published}
            onChange={(e) => set("published", e.target.checked)}
            data-testid="editor-published"
          />
          <span>Veröffentlicht (sichtbar auf /blog)</span>
        </label>

        <div className="admin-form-foot">
          <Link to="/admin" className="btn btn--ghost">
            <span>Abbrechen</span>
          </Link>
          <button
            type="submit"
            className="btn btn--solid"
            disabled={submitting}
            data-testid="editor-submit"
          >
            <span>{submitting ? "Speichert…" : isEdit ? "Speichern" : "Beitrag anlegen"}</span>
            <span className="btn__arrow">→</span>
          </button>
        </div>
      </form>
    </div>
  );
}
